import { Service } from '@genial/services';
import { Utils } from '@genial/utils';
import { selector } from 'recoil';

import { partnerIdToApisSelector, advisorIdToApisSelector } from '../master/selector';

import {
  selectedConversionYearState,
  selectedFinancialMovementYearState,
  selectedInvestmentResumeYearState,
} from './atom';
import { getProductDistributionMap, screenName } from './constants';
import { NormalizedDistributionProduct } from './types';

export const conversionDataQuery = selector({
  key: 'ManagementAnalysis/ConversionDataQuery',
  get: async ({ get }) => {
    const year = get(selectedConversionYearState);
    const advisorSelected = get(advisorIdToApisSelector);
    const partnerSelected = get(partnerIdToApisSelector);

    try {
      const { data } = await Service.ReportingServices.V1.getReportingConversionByYear({
        screenName,
        advisorId: advisorSelected,
        partnerId: partnerSelected,
        year: parseInt(year, 10),
      });

      return {
        information: data.information,
        conversion: data.conversion?.sort((a, b) => a.month - b.month),
      };
    } catch (_error) {
      return undefined;
    }
  },
});

export const financialMovementDataQuery = selector({
  key: 'ManagementAnalysis/FinancialMovementDataQuery',
  get: async ({ get }) => {
    const year = get(selectedFinancialMovementYearState);
    const advisorSelected = get(advisorIdToApisSelector);
    const partnerSelected = get(partnerIdToApisSelector);

    try {
      const { data } = await Service.ReportingServices.V1.getReportingFinancialMovementByYear({
        screenName,
        advisorId: advisorSelected,
        partnerId: partnerSelected,
        year: parseInt(year, 10),
      });

      return {
        information: data.information,
        financialMovement: data.financialMovement?.sort((a, b) => a.month - b.month),
      };
    } catch (_error) {
      return undefined;
    }
  },
});

export const investmentResumeDataQuery = selector({
  key: 'ManagementAnalysis/InvestmentResumeDataQuery',
  get: async ({ get }) => {
    const year = get(selectedInvestmentResumeYearState);
    const advisorSelected = get(advisorIdToApisSelector);
    const partnerSelected = get(partnerIdToApisSelector);

    const { data } = await Service.ReportingServices.V1.getReportingProductDistributionByYear({
      screenName,
      advisorId: advisorSelected,
      partnerId: partnerSelected,
      year: parseInt(year, 10),
    });

    const normalizedData: NormalizedDistributionProduct[] = (data.productDistribution || [])
      .map((item) => ({
        ...item,
        color: getProductDistributionMap(item.productClassName).color,
        label: getProductDistributionMap(item.productClassName).fullName,
        productClassValueFormatted: Utils.Mask.Money(item.productClassValue),
        productClassPercentageFormatted:
          item.productClassPercentageFormatted ||
          Utils.Mask.Percentage(item.productClassPercentage * 100),
        productClassPercentage: item.productClassPercentage * 100,
        productClassName: item.productClassName as string,
        productSubClassData: item.productSubClassData
          ?.map((subItem) => ({
            ...subItem,
            productSubClassPercentage: parseFloat(
              (subItem.productSubClassPercentage * 100).toFixed(2)
            ),
            productSubClassPercentageFormatted:
              subItem.productSubClassPercentageFormatted ||
              Utils.Mask.Percentage(subItem.productSubClassPercentage * 100),
          }))
          .sort((a, b) => b.productSubClassPercentage - a.productSubClassPercentage),
      }))
      .sort((a, b) => b.productClassPercentage - a.productClassPercentage);

    return {
      information: {
        ...data.information,
        mainInvestedClassNormalized:
          getProductDistributionMap(data.information.mainInvestedClass)?.fullName || '--',
        mainInvestedSubClassNormalized: data.information.mainInvestedSubClass || '--',
      },
      productDistribution: normalizedData,
    };
  },
});

export const checkingsAccountDataQuery = selector({
  key: 'ManagementAnalysis/CheckingsAccountDataQuery',
  get: async ({ get }) => {
    try {
      const advisorSelected = get(advisorIdToApisSelector);
      const partnerSelected = get(partnerIdToApisSelector);

      const { data } = await Service.ReportingServices.V1.getAllReportingBalance({
        screenName,
        advisorId: advisorSelected,
        partnerId: partnerSelected,
      });

      return {
        availableBalance: data.balance.availableBalance,
        availableBalanceNormalized: Utils.Mask.Money(data.balance.availableBalance),
        updatedAt: data.balance.updatedAt,
        updatedAtNormalized: Utils.Mask.DateToNow(data.balance.updatedAt),
      };
    } catch (_error) {
      return undefined;
    }
  },
});

export type NormalizedAdvisorData = { label: string; value: string }[] | null | undefined;

export const listAdvisorsDataQuery = selector({
  key: 'ManagementAnalysis/ListAdvisorsDataQuery',
  get: async () => {
    const { data } = await Service.ReportingServices.V1.getReportingAdvisorListing({
      screenName,
      partnerId: 1,
    });

    const advisorsFromApi = (data?.map((item) => ({
      label: item.advisorName || '',
      value: item.advisorId.toString(),
    })) || []) as NonNullable<NormalizedAdvisorData>;

    return data ? [{ label: 'Todos', value: '' }, ...advisorsFromApi] : data;
  },
});

export const selectedadvisorSelected = selector({
  key: 'ManagementAnalysis/SelectadvisorSelected',
  get: ({ get }) => {
    const advisorSelected = get(advisorIdToApisSelector);

    return advisorSelected;
  },
});
